import videojs from 'video.js';

import Config from './config';
import Analytics from '../plugins/analytics';
import eventsSrv from './events';
import vimmiPI from '../plugins/vimmiPI';
import vjsSrv from './vjsSrv';
import { normalizeLang } from 'vimmi-web-utils/cjs/normalizeLang';
import { PLAYER_EVENTS } from 'vimmi-web-utils/cjs/postMessage';
import AudioTracksSrv from './audioTracksSrv';
import PlayerSrv from 'src/js/services/playerSrv';
import { AppError } from 'vimmi-web-utils/cjs/errors/appError';
import { ErrorCodes } from 'vimmi-web-utils/cjs/errors/errorCodes';
import $logger from '../logger';
import dashJs from 'dashjs';

export default class TechSrv {
  constructor() {
    this.prevActiveBitrateId = null;
    this.activeNativeAudioTrack = null;
    this.activeTextTrack = null;
    this.representations = [];
    this.trackToActivate = null;
    this.dashStats = {
      totalTraffic: 0
    };

    this.registerHooks();

    vjsSrv.vjsPlayer.on('loadstart', () => {
      this.dashStats = {
        totalTraffic: 0
      };
      this.representations = [];
    });

    vjsSrv.vjsPlayer.on('mediachange', (event) => {
      this._onBitrateChanged(event);
    });

    vjsSrv.vjsPlayer.on('playing', () => {
      if (vjsSrv.vjsPlayer.audioTracks().length >= 2) {
        AudioTracksSrv.disableCustomButton();
      }
      if (this.trackToActivate) {
        this.changeTrack(this.trackToActivate);
        vimmiPI.getActiveAsset().set('extraData.appliedPrefLang', true);
        this.trackToActivate = null;
      }
    });

    vjsSrv.vjsPlayer.on('loadedmetadata', (event) => {
      this.handleAspectRatioByStream(vimmiPI.getActiveAsset());
      this._onLoadedmetadata(event);
    });

    this.watchTextChanges();
    this.watchAudioChanges();
  }

  get tech() {
    if ((vimmiPI.getActiveAsset().get('activePlayback.drm.type') || '').toLowerCase().indexOf('dash') !== -1) {
      return vjsSrv.vjsPlayer.dash;
    } else {
      return vjsSrv.vjsPlayer.tech({ IWillNotUseThisInPlugins: true }).vhs;
    }
  }

  static xhrHook() {
    let lastRequestsCountTime = Date.now();

    videojs.Vhs.xhr.beforeRequest = function(options) {
      let asset = vimmiPI.getActiveAsset();
      options.beforeSend = request => {
        if (asset) {
          request.addEventListener('loadend', () => {
            let tech = vjsSrv.vjsPlayer.tech({ IWillNotUseThisInPlugins: true });
            let vhsStat = tech.vhs && tech.vhs.stats ? tech.vhs.stats : {};
            if (request.status >= 400 || request.status < 200) {
              asset.set('extraData.xhrError', {
                method: request.method,
                status: request.status,
                statusText: request.statusText,
                responseURL: request.responseURL
              });
            } else {
              asset.set('extraData.xhrError', null);
            }

            if (request.status >= 500 || request.status < 200) {
              let mediaRequests = 0;
              if (lastRequestsCountTime + 60000 < Date.now()) {
                lastRequestsCountTime = vhsStat.mediaRequests;
              }


              if (mediaRequests > 10 && vhsStat.mediaRequestsErrored) {
                if (100 * (vhsStat.mediaRequestsErrored / vhsStat.mediaRequests) > 60) {
                  vjsSrv.vjsPlayer.internalError = new AppError(ErrorCodes.CDN_ERR, 'Too many failed requests!', '');
                  vjsSrv.vjsPlayer.pause();
                  vjsSrv.vjsPlayer.hasStarted(false);
                  vjsSrv.vjsPlayer.currentTime(0);
                  vjsSrv.vjsPlayer.reset();
                  vjsSrv.vjsPlayer.error(2);
                }
              }
            }
          }, true);
        }
      };
      if (asset && asset.get('origin') === 'aws') {
        let activeManifest = asset.get('activePlayback.url');
        if (options.uri !== activeManifest) {
          let parts = activeManifest.split('?');
          if (parts.length >= 2) {
            options.uri += options.uri.indexOf('?') !== -1 ? '&' : '?' + parts[1];
          }
        }
      }
      return options;
    };
  }

  registerHooks() {
    if (videojs.Html5DashJS) {
      const $this = this;

      const beforeinitialize = (player, mediaPlayer) => {
        this.dashStats.totalTraffic = 0;
        /// dirty hook to get DASH bytes
        mediaPlayer.extend('RequestModifier', function() {
          return {
            modifyRequestHeader: function(xhr) {
              setTimeout(() => {
                let t = xhr.onprogress;
                xhr.onprogress = function(event) {
                  if (event.lengthComputable) {
                    $this.dashStats.totalTraffic += parseInt((event.total) / 8); // bits to bytes
                  }
                  t(event);
                };
              });
              return xhr;
            },
            modifyRequestURL: function(url) {
              return url;
            }
          };
        });

        mediaPlayer.on(dashJs.MediaPlayer.events.STREAM_INITIALIZED, (e) => {
          player.dash.representations = this._createRepresentations(mediaPlayer);
        });

        mediaPlayer.on(dashJs.MediaPlayer.events.QUALITY_CHANGE_RENDERED, (e) => {
          if (e.mediaType === 'video') {
            this._onBitrateChanged(e);
          }
        });
      };

      const updatesource = (source) => {
        if (vimmiPI.getActiveAsset().get('activePlayback.drm.type') === 'DASH/WIDEVINE') {
          let options = PlayerSrv.getVideoJsSrc(vimmiPI.getActiveAsset().get('activePlayback'), vimmiPI.getActiveAsset());
          if (options.keySystemOptions) {
            source.keySystemOptions = options.keySystemOptions;
          }
        }
        return source;
      };

      videojs.Html5DashJS.hook('beforeinitialize', beforeinitialize);
      videojs.Html5DashJS.hook('updatesource', updatesource);

      vjsSrv.vjsPlayer.one('dispose', () => {
        videojs.Html5DashJS.removeHook('updatesource', updatesource);
        videojs.Html5DashJS.removeHook('beforeinitialize', beforeinitialize);
      });
    }
  }


  get stats() {
    let streamBandwidth = 0;
    let streamBitrate = 0;
    let averageBandwidth = 0;
    let averageBitrate = 0;
    let bytesReceived = 0;

    const bitrateInterval = 5;

    let tech = this.tech;

    if ((vimmiPI.getActiveAsset().get('activePlayback.drm.type') || '').toLowerCase().indexOf('dash') !== -1 && tech?.mediaPlayer?.getDashAdapter && this.activeBitrate) {
      /** dashjs>3.0.0*/
      let mediaPlayer = tech.mediaPlayer;
      let activeStream = mediaPlayer.getActiveStream();
      let streamInfo = activeStream.getStreamInfo();
      let dashAdapter = mediaPlayer.getDashAdapter();
      let dashMetrics = mediaPlayer.getDashMetrics();

      if (dashMetrics && streamInfo) {
        const periodIdx = streamInfo.index;
        let repSwitch = dashMetrics.getCurrentRepresentationSwitch('video', true);
        streamBandwidth = Math.round(dashAdapter.getBandwidthForRepresentation(repSwitch.to, periodIdx) / 8); // 'byte/s'
        // streamBitrate = repSwitch ? Math.round(streamBandwidth / 1000) * 8 : NaN; // 'kbps'
        streamBitrate = repSwitch ? Math.round(streamBandwidth) * 8 : NaN; // 'kbps'
      }

      averageBitrate = this._calculatedBitrate || 0;
      averageBandwidth = this._calculatedBandwidth || 0;
      bytesReceived = this.dashStats.totalTraffic;
      if (!this.bitrateCalculator) {
        this.lastBytesReceived = this.lastBytesReceived || 0;
        this.bitrateCalculator = setInterval(() => {
          if (!vjsSrv.vjsPlayer.paused()) {
            this._calculatedBitrate = Math.round((((this.dashStats.totalTraffic - this.lastBytesReceived)) * 8) / bitrateInterval);
            this._calculatedBandwidth = Math.round(((this.dashStats.totalTraffic - this.lastBytesReceived)) / bitrateInterval);
            this.lastBytesReceived = this.dashStats.totalTraffic;
          }
        }, bitrateInterval * 1000);
      }
    } else if (tech?.stats && this.activeBitrate) {
      if (this.bitrateCalculator) {
        clearInterval(this.bitrateCalculator);
      }
      let vhsStat = tech.stats;
      bytesReceived = vhsStat.mediaBytesTransferred;
      if (vimmiPI.getActiveAsset() && vimmiPI.getActiveAsset().get('extraData.missedBytes')) {
        bytesReceived += vimmiPI.getActiveAsset().get('extraData.missedBytes');
      }
      /// vhsStat.bandwidth = bits/seconds

      /** convert bits to bytes */
      averageBandwidth = Math.round(vhsStat.bandwidth / 8); // 'byte/s'
      // averageBitrate = Math.round(vhsStat.bandwidth / 1024); // 'kbps'
      averageBitrate = Math.round(vhsStat.bandwidth); // 'kbps'

      streamBandwidth = Math.round(this.activeBitrate.bandwidth / 8); // 'byte/s'
      // streamBitrate = Math.round(this.activeBitrate.bandwidth / 1024); // 'kbps'
      streamBitrate = Math.round(this.activeBitrate.bandwidth); // 'kbps'
    }

    return {
      averageBandwidth,
      streamBandwidth,
      averageBitrate,
      streamBitrate,
      bytesReceived
    };
  }


  getRepresentations() {
    return this.representations;
  }

  get activeBitrate() {
    if (this._activeBitrate) {
      return this._activeBitrate;
    }


    const tech = this.tech;

    if (tech?.mediaPlayer) {
      let qualityIndex = tech.mediaPlayer.getQualityFor('video');
      this._activeBitrate = this.representations.find((item) => {
        return item.qualityIndex === qualityIndex;
      });

    } else if (tech?.selectPlaylist) {
      let selectedPlaylist = tech.selectPlaylist();
      if (selectedPlaylist) {
        let selectedPlaylistBandwidth = selectedPlaylist?.playlist?.attributes?.BANDWIDTH || selectedPlaylist?.attributes?.BANDWIDTH || selectedPlaylist?.bandwidth || 0;
        this._activeBitrate = this.representations.find((item) => {
          return item.bandwidth === selectedPlaylistBandwidth;
        });
      }
    }
    return this._activeBitrate || null;

  }

  _onBitrateChanged(event) {
    /** total bitrate switches*/
    if (!vimmiPI.getActiveAsset()) {
      return;
    }
    let switches = vimmiPI.getActiveAsset().get('extraData.jump') || 0;
    vimmiPI.getActiveAsset().set('extraData.jump', switches + 1);

    /** maximum bitrate enabled*/
    let maxViewBitrate = vimmiPI.getActiveAsset().get('extraData.max_view_bitrate') || 0;
    this._activeBitrate = null;
    let active = this.activeBitrate;
    if (active) {
      let prevActive = vimmiPI.getActiveAsset().get('extraData.active_stream');
      let allBitrates = this.getRepresentations();

      if (prevActive !== active._id + 1) {
        vimmiPI.getActiveAsset().set('extraData.active_stream', active._id + 1);
        Analytics.send('media_bitrate_switch', {
          cur_bitrate: prevActive && allBitrates[prevActive - 1] ? allBitrates[prevActive - 1].bandwidth || 0 : 0,
          tar_bitrate: active.bandwidth || 0
        });
      }
    }
    let activeBandwidth = active?.playlist?.attributes?.BANDWIDTH || active?.attributes?.BANDWIDTH || active?.bandwidth;

    if (activeBandwidth > maxViewBitrate) {
      vimmiPI.getActiveAsset().set('extraData.max_view_bitrate', +activeBandwidth);
    }
    eventsSrv.emit('videoBitrateChanged', { current: active });
  }

  _onLoadedmetadata(event) {

    if (!vimmiPI.getActiveAsset()) {
      return;
    }
    let maxBitrate = 0;
    let minBitrate = 0;

    const tech = this.tech;

    if (tech?.representations) {
      this.representations = tech.representations()
        .sort(function(a, b) {
          return +a.bandwidth < +b.bandwidth;
        });
      this.representations.map((el, index) => {
        el._id = index;
        if (maxBitrate < el.bandwidth) {
          maxBitrate = el.bandwidth;
        }

        minBitrate = minBitrate || el.bandwidth; // need for set initial value for minbitrate
        if (minBitrate > el.bandwidth) {
          minBitrate = el.bandwidth;
        }
      });
    } else {
      this.representations = [];
    }
    let active = this.activeBitrate;
    if (active) {
      let maxViewBitrate = vimmiPI.getActiveAsset().get('extraData.max_view_bitrate') || 0;
      let activeBandwidth = active.bandwidth || 0;
      if (active > maxViewBitrate) {
        vimmiPI.getActiveAsset().set('extraData.max_view_bitrate', +activeBandwidth);
      }
      vimmiPI.getActiveAsset().set('extraData.active_stream', active._id + 1);
    }

    vimmiPI.getActiveAsset().set('extraData.max_bitrate', maxBitrate);
    vimmiPI.getActiveAsset().set('extraData.min_bitrate', minBitrate);
    vimmiPI.getActiveAsset().set('extraData.n_streams', this.representations.length);


    setTimeout(() => {

      /** init */
      this.activeNativeAudioTrack = this._findActiveAudioTrack();
      this.activeTextTrack = this._findActiveTextTrack();
    }, 100);
    eventsSrv.emit('app_representation_loads');
  }

  _createRepresentation(bitrateInfo, index, enabledCallback) {
    let representation = {};
    // dash.js does not expose the representation ID in the manifest
    representation.id = bitrateInfo.qualityIndex || index;
    representation.width = bitrateInfo.width;
    representation.height = bitrateInfo.height;
    representation.bandwidth = bitrateInfo.bandwidth;
    representation.qualityIndex = bitrateInfo.qualityIndex || index;
    representation.isEnabled_ = true;
    representation.enabled = function(enable, isAutoBitrateEnabled) {
      if (enable === undefined) {
        return representation.isEnabled_;
      }

      if (enable === representation.isEnabled_) {
        /** for dash active*/
        // if (enable) {
        //     enabledCallback(enable);
        // }

        return;
      }

      if (enable === true || enable === false) {
        representation.isEnabled_ = enable;

        enabledCallback(enable, isAutoBitrateEnabled);
      }
    };

    return representation;
  }

  _createRepresentations(mediaPlayer_) {

    let streamInfo = mediaPlayer_.getActiveStream().getStreamInfo();
    const periodIdx = streamInfo.index;
    if (!mediaPlayer_.getDashAdapter) {
      console.warn('mediaPlayer_.getDashAdapter is not a function!');
      return;
    }
    let dashAdapter = mediaPlayer_.getDashAdapter();
    let adaptation = dashAdapter.getAdaptationForType(periodIdx, 'video', streamInfo);


    let representations = adaptation.Representation_asArray.map((qualityLevel, index) => {
      this.dashStats.maxQualityLevel = !this.dashStats.maxQualityLevel ? qualityLevel : (this.dashStats.maxQualityLevel.bandwidth < qualityLevel.bandwidth) ? qualityLevel : this.dashStats.maxQualityLevel;
      return this._createRepresentation(qualityLevel, index, (state, isAutoBitrateEnabled) => {

        if (isAutoBitrateEnabled) {
          if (!this.dashStats.autoBitrateEnabled && this.dashStats.maxQualityLevel) {
            mediaPlayer_.updateSettings({
              'streaming': {
                'abr': {
                  'maxBitrate': {
                    'video': parseInt((this.dashStats.maxQualityLevel.bandwidth + 1000) / 1000, 10)
                  }
                }
              }
            });
            this.dashStats.autoBitrateEnabled = true;
          }

        } else {
          this.dashStats.autoBitrateEnabled = false;
          if (state) {
            mediaPlayer_.updateSettings({
              'streaming': {
                'abr': {
                  'maxBitrate': {
                    'video': parseInt((qualityLevel.bandwidth + 1000) / 1000)
                  }
                  // 'limitBitrateByPortal': false
                }
              }
            });
          }
        }


      });
    });
    return () => {
      return representations;
    };

  }

  _findActiveAudioTrack() {
    let audioTracks = vjsSrv.vjsPlayer.audioTracks();
    for (let i = 0; i < audioTracks.length; i++) {
      let track = audioTracks[i];
      if (track.enabled) {
        return track;
      }
    }
  }

  _findActiveTextTrack() {
    let textTracks = vjsSrv.vjsPlayer.textTracks();

    for (let i = 0; i < textTracks.length; i++) {
      let track = textTracks[i];
      if (track.enabled) {
        return track;
      }
    }
  }

  getActiveAudioTrack() {
    let track = this.activeNativeAudioTrack ? (this.activeNativeAudioTrack.language || this.activeNativeAudioTrack.label).toLowerCase() :
      null;
    const asset = vimmiPI.getActiveAsset();
    return asset && vimmiPI.isApplicableMetadataLang(asset) ? asset.get('metadataLang') || null : track;
  }

  watchAudioChanges() {
    let audioTracks = vjsSrv.vjsPlayer.audioTracks();

    audioTracks.addEventListener('change', () => { /// Fired when a track has been enabled or disabled.

      let from = this.getActiveAudioTrack();

      for (let i = 0; i < audioTracks.length; i++) {
        let track = audioTracks[i];
        if (track.enabled) {
          this.activeNativeAudioTrack = track;
          Analytics.send('media_audio_switch', {
            cur_audio_lang: from,
            tar_audio_lang: (track.language || track.label || 'unknown').toLowerCase()
          });
          return;
        }
      }
    });

    let lastAddedTime = 0;
    let addedDiff = 0;
    let addTimeout;

    audioTracks.addEventListener('addtrack', (event) => { // Fired when a new audio track has been added to the media element.

      if (!vimmiPI.getActiveAsset()) {
        return;
      }
      let track = event.track || null;

      if (!track) {
        return;
      }

      if (!lastAddedTime) {
        lastAddedTime = Date.now();
      } else {
        addedDiff = Date.now() - lastAddedTime;
      }

      if (addTimeout) {
        clearTimeout(addTimeout);
        addTimeout = null;
      }
      addTimeout = setTimeout(() => {
        this.onAudioTracksLoaded();
        lastAddedTime = 0;
        addTimeout = null;
      }, addedDiff + 1000);
    });
  }

  watchTextChanges() {
    let textTracks = vjsSrv.vjsPlayer.textTracks();
    // Logger.log('#watchTextChanges', {
    //     textTracks: textTracks
    //
    // });
    textTracks.addEventListener('change', function() {
      for (let i = 0; i < textTracks.length; i++) {
        let track = textTracks[i];
        if (track.mode === 'showing') {
          Analytics.send('media_subtitle_switch', {
            cur_subtitle_lang: this.activeTextTrack ? this.activeTextTrack.language || this.activeTextTrack.label || 'unknown' :
              null,
            tar_subtitle_lang: track.language || track.label || 'unknown'
          });

          this.activeTextTrack = track;
          return;
        }
      }
      Analytics.send('media_subtitle_switch', {
        cur_subtitle_lang: this.activeTextTrack ? this.activeTextTrack.language || this.activeTextTrack.label || 'unknown' :
          null,
        tar_subtitle_lang: 'unknown'

        // from: this.activeTextTrack ? this.activeTextTrack.language || this.activeTextTrack.label || 'unknown' :
        //     null,
        // to: null
      });
    });
  }

  /**
   * @method handleAspectRatioByContent
   * @description check content aspect ratio before content start playing, by item fields live 'media_aspect_ratio', 'media_resolution', 'media_is_vertical', 'orientation'
   * */
  handleAspectRatioByItem(asset) {
    const customer = (Config.get(`params.portal.customer`) || '').toLowerCase();
    const deviceOri = window.screen.availWidth / window.screen.availHeight < 1 ? 'vertical' : 'horizontal';
    const ratio = asset.get('item.media_aspect_ratio'); // ex: "0.562"
    const resolution = asset.get('item.media_resolution'); // ex: "1080x1920"
    const isVertical = asset.get('item.media_is_vertical'); // bool
    const orientation = asset.get('item.orientation'); // "vertical"/"horizontal"/"both", both?

    let contentOri;
    if (isVertical) {
      contentOri = 'vertical';
    } else if (orientation) {
      contentOri = orientation === 'vertical' ? 'vertical' : 'horizontal';
    } else if (ratio) {
      contentOri = +ratio < 1 ? 'vertical' : 'horizontal';
    } else if (resolution) {
      const [width, height] = resolution.split(':');
      contentOri = +width / +height < 1 ? 'vertical' : 'horizontal';
    }
    vjsSrv.vjsPlayer.removeClass('object-fit-contain');
    vjsSrv.vjsPlayer.removeClass('object-fit-cover');
    vjsSrv.vjsPlayer.removeClass('object-fit-fill');
    if (contentOri) {
      if (contentOri === 'horizontal') {
        if (deviceOri === 'vertical' && customer === 'verb' && asset.isLive) {
          vjsSrv.vjsPlayer.addClass('object-fit-cover');
        }
      }
      asset.set('calcOrientation', contentOri);
    }
    return contentOri || null;
  }

  /**
   * @method handleAspectRatioByStream
   * @description check video bitrates aspect ratio, use the method if the "handleAspectRatioByItem" method returns nothing
   * */
  handleAspectRatioByStream(asset) {
    let activeBitrate = this.activeBitrate;
    if (activeBitrate && activeBitrate.width && activeBitrate.height && this.prevActiveBitrateId !== activeBitrate._id) {
      this.prevActiveBitrateId = activeBitrate._id;
      let orientation = activeBitrate.width / activeBitrate.height < 1 ? 'vertical' : 'horizontal';
      if (!asset.get('calcOrientation')) {
        if (orientation === 'vertical') {
          vjsSrv.vjsPlayer.addClass('object-fit-contain');
        }
      } else if (asset.get('calcOrientation') !== orientation) {
        $logger.p('handleAspectRatioByStream').warn(`item has orientation - ${asset.get('calcOrientation')}, but stream - ${orientation}`);
      }
    }
  }

  onAudioTracksLoaded() {
    let audioTrackList = vjsSrv.vjsPlayer.audioTracks();
    $logger.p('event').info('audioTracksLoaded', audioTrackList);

    const asset = vimmiPI.getActiveAsset();
    if (!audioTrackList.length || !asset) {
      return;
    }

    let tracksList = [];
    let activeTrackId = null;

    if (audioTrackList.length) { /// format list based on manifest response
      this.activeNativeAudioTrack = audioTrackList[0]; // by default take first audio track
      activeTrackId = this.activeNativeAudioTrack.id;
      tracksList = audioTrackList.tracks_.map((track) => {
        let trackLang = track.language ? normalizeLang(track.language, 'iso-2') : null;
        return {
          id: track.id,
          language: trackLang,
          label: track.label || normalizeLang(trackLang, 'name'),
          type: 'default'
        };
      });
    }


    if (audioTrackList.length >= 2) { // handle manifest languages
      AudioTracksSrv.disableCustomButton();
      audioTrackList.tracks_.map((track) => {
        if (track.enabled) {
          this.activeNativeAudioTrack = track;
          activeTrackId = this.activeNativeAudioTrack.id;
        }
      });
      let prefLang = normalizeLang(Config.get('params.portal.preferred_audio_lang'), 'iso-2');
      if (prefLang && !asset.get('extraData.appliedPrefLang')) {
        this.trackToActivate = audioTrackList.tracks_.find(track => {
          let trackLang = track.language ? normalizeLang(track.language, 'iso-2') : null;
          return (trackLang === prefLang || (track.label || '').toLowerCase().indexOf(prefLang) !== -1);
        });

        $logger.p('preferredTrack').info({
          prefLang, trackToActivate: this.trackToActivate
        });
      }
    } else if (vimmiPI.isApplicableMetadataLang(asset)) {  // handle metadata languages
      asset.set('extraData.appliedPrefLang', true);
      if (Config.get('params.controlBar.AudioTrackButton')) {
        AudioTracksSrv.enableCustomButton();
      }
      tracksList = asset.get('mAudio').map((lang) => {
        return { id: lang, language: lang, label: normalizeLang(lang, 'name'), type: 'custom' };
      });
      activeTrackId = asset.get('metadataLang');
    } else if (Config.get('params.controlBar.AudioTrackButton')) {
      AudioTracksSrv.disableCustomButton();
    }

    /** to display default language that configured in the AMS */
    if (audioTrackList.length < 2 && !vimmiPI.isApplicableMetadataLang(asset) && asset.get('mAudio') && asset.get('mAudio').length) {
      if (tracksList.length) {
        tracksList[0].language = asset.get('mAudio')[0];
        tracksList[0].label = normalizeLang(asset.get('mAudio')[0], 'name');
      }
    }


    eventsSrv.emit(PLAYER_EVENTS.AUDIO_TRACKS_LOADED, tracksList);
    if (!this.trackToActivate && activeTrackId) {
      eventsSrv.emit(PLAYER_EVENTS.AUDIO_TRACK_CHANGED, activeTrackId);
    }
  }

  /**
   *
   * @param newTrack{AudioTrack|Object}
   * @param newTrack.id {string} id of track
   * @param newTrack.label {string} label of track
   * @param newTrack.language {string} language of track
   * @param newTrack.type {'default'|'custom'} language of track
   * */

  changeTrack(newTrack) {
    $logger.p('changeTrack').debug({
      from: this.getActiveAudioTrack(),
      to: newTrack,
      type: newTrack?.type,
      metadataAudio: vimmiPI.getActiveAsset().get('mAudio'),
      metadataLang: vimmiPI.getActiveAsset().get('metadataLang')
    });
    if (newTrack?.type === 'default' || newTrack?.addEventListener) {
      const trackList = vjsSrv.vjsPlayer.audioTracks();

      trackList.tracks_.map((_track) => {
        if (_track.id === newTrack.id) {
          this.activeNativeAudioTrack = _track;
        } else {
          _track.enabled = false;
        }
      });

      if (this.activeNativeAudioTrack) {
        this.activeNativeAudioTrack.enabled = true;
        eventsSrv.emit(PLAYER_EVENTS.AUDIO_TRACK_CHANGED, this.activeNativeAudioTrack.id);
      }
    } else if (newTrack?.type === 'custom') {
      if (!vimmiPI.isApplicableMetadataLang(vimmiPI.getActiveAsset())) {
        return;
      }

      let cur_audio_lang = vimmiPI.getActiveAsset().get('metadataLang');

      if (newTrack.language === cur_audio_lang) {
        return;
      }

      if (!vimmiPI.getActiveAsset().get('isLive')) {
        vimmiPI.getActiveAsset().set('playerOptions.startPosition', vjsSrv.vjsPlayer.currentTime());
      }

      vimmiPI.getActiveAsset().set('state.changingAudio', true);


      let cur_media_session_id = vimmiPI.getActiveAsset().get('media_session_id');

      vjsSrv.vjsPlayer.addClass('custom-waiting-video');

      vimmiPI.applyNewMetadataLang(newTrack.language)
        .then((result) => {
          Analytics.send('media_audio_switch', {
            media_session_id: (Config.get(`params.portal.user_id`) || Config.get(`params.portal.temp_user_id`) || 'unknown') + '_' + cur_media_session_id,
            cur_audio_lang: cur_audio_lang,
            tar_audio_lang: newTrack.language
          });
          vjsSrv.vjsPlayer.hasStarted(true);
          eventsSrv.emit(PLAYER_EVENTS.AUDIO_TRACK_CHANGED, newTrack.language);
        })
        .catch((error) => {
          $logger.p('changeTrack/metadata').error(error);
        });
    }
  }

  destroy() {
    if (this.bitrateCalculator) {
      clearInterval(this.bitrateCalculator);
      this.bitrateCalculator = null;
    }
  }
}